import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { thunks as PlaylistThunks } from 'reducers/PlayList'
import { trackEvent } from 'utils/segmentService'

const usePlaylistRemoval = ({ onRemoved } = {}) => {
  const dispatch = useDispatch()
  const mountedRef = useRef(true)
  const pendingRequestRef = useRef(null)
  const removalPendingRef = useRef(false)
  const [pendingRequest, setPendingRequest] = useState(null)
  const [hasRemovalError, setHasRemovalError] = useState(false)
  const [isRemovalPending, setIsRemovalPending] = useState(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const requestPlaylistRemoval = useCallback((playlist, source = 'unknown') => {
    if (!playlist?.playlistid || removalPendingRef.current) return

    const request = { playlist, source }
    pendingRequestRef.current = request
    setPendingRequest(request)
    setHasRemovalError(false)
  }, [])

  const cancelPlaylistRemoval = useCallback(() => {
    if (removalPendingRef.current) return

    pendingRequestRef.current = null
    setPendingRequest(null)
    setHasRemovalError(false)
  }, [])

  const confirmPlaylistRemoval = useCallback(async () => {
    const request = pendingRequestRef.current
    if (!request || removalPendingRef.current) return false

    removalPendingRef.current = true
    setHasRemovalError(false)
    setIsRemovalPending(true)

    try {
      await dispatch(PlaylistThunks.deletePlaylist(request.playlist))
    } catch {
      if (mountedRef.current) setHasRemovalError(true)
      return false
    } finally {
      removalPendingRef.current = false
      if (mountedRef.current) setIsRemovalPending(false)
    }

    pendingRequestRef.current = null
    if (mountedRef.current) setPendingRequest(null)

    // An analytics failure must not turn a successful deletion into a retry.
    try {
      trackEvent('Playlist Removed', {
        playlist_id: String(request.playlist.playlistid),
        source: request.source
      })
    } catch (error) {
      console.warn(error)
    }

    if (mountedRef.current) onRemoved?.(request.playlist)
    return true
  }, [dispatch, onRemoved])

  return {
    cancelPlaylistRemoval,
    confirmPlaylistRemoval,
    hasRemovalError,
    isRemovalOpen: Boolean(pendingRequest),
    isRemovalPending,
    pendingRemovalPlaylist: pendingRequest?.playlist || null,
    requestPlaylistRemoval
  }
}

export default usePlaylistRemoval
